import { type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../contexts/AuthContext';
import { ME_QUERY_KEY } from '../../contexts/AuthContext';
import type { Me } from '@/api/generated';

interface ProtectedRouteProps {
  children: ReactNode;
}

const ProtectedRoute = ({ children }: ProtectedRouteProps) => {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();
  const queryClient = useQueryClient();

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-24 text-wayne-ink-3">
        <Loader2 size={28} className="animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // First-time user redirect: if hasOnboarded is false and we're not already
  // on the onboarding page, redirect there. Returning users (hasOnboarded: true)
  // go straight to their requested page.
  const me = queryClient.getQueryData<Me>(ME_QUERY_KEY);
  if (me && !me.hasOnboarded && !location.pathname.startsWith('/onboarding')) {
    return <Navigate to="/onboarding" replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
